import { Injectable } from '@angular/core';

import { combineLatest, debounceTime, map, Observable, of, startWith } from 'rxjs';

import {
  injectTellentContextCollectors,
  injectTellentContextRedactor,
} from '@core/tellent-assistant-contracts';
import { AppContext, AppContextSchema, EMPTY_APP_CONTEXT } from '@core/tellent-assistant-types';
import { enforceSizeLimit } from '@core/tellent-assistant-util';

@Injectable({ providedIn: 'root' })
export class TellentAssistantAppContextService {
  private readonly collectors = injectTellentContextCollectors({ optional: true }) ?? [];
  private readonly redactor = injectTellentContextRedactor({ optional: true });

  public readonly changes$: Observable<AppContext>;

  constructor() {
    this.changes$ =
      this.collectors.length === 0
        ? of(EMPTY_APP_CONTEXT)
        : combineLatest(
            this.collectors.map(c =>
              (c.changes$ ?? of(c.snapshot())).pipe(
                startWith(c.snapshot()),
                map(value => ({ key: c.key, value })),
              ),
            ),
          ).pipe(
            debounceTime(50),
            map(entries => this.finalize(this.merge(entries))),
          );
  }

  public snapshot(): AppContext {
    const raw = this.merge(this.collectors.map(c => ({ key: c.key, value: c.snapshot() })));
    return this.finalize(raw);
  }

  private merge(entries: ReadonlyArray<{ key: string; value: unknown }>): Record<string, unknown> {
    const base: Record<string, unknown> = {
      route: EMPTY_APP_CONTEXT.route,
      dialogs: [],
      visibleEntities: [],
      selection: [],
      meta: {},
    };
    for (const { key, value } of entries) {
      if (key.startsWith('meta.')) {
        const metaKey = key.slice('meta.'.length);
        (<Record<string, unknown>>base.meta)[metaKey] = value;
      } else if (['visibleEntities', 'selection', 'dialogs'].includes(key)) {
        const current = <unknown[]>base[key] ?? [];
        const incoming = Array.isArray(value) ? <unknown[]>value : [];
        base[key] = [...current, ...incoming];
      } else {
        base[key] = value;
      }
    }
    return base;
  }

  private finalize(raw: Record<string, unknown>): AppContext {
    const parsed = AppContextSchema.safeParse(raw);
    const ctx = parsed.success ? parsed.data : EMPTY_APP_CONTEXT;
    const limited = enforceSizeLimit(ctx);
    const redacted = <AppContext | undefined>this.redactor?.redact(limited);
    return redacted ?? limited;
  }
}
